/**
 * WP-07A — Stable evidence failure vocabulary.
 *
 * Same discipline as `loops/lifecycle/errors.ts`: a small categorical set, no SQL
 * text, no provider message, no constraint name, no object path, no file name,
 * no actor id, no workspace id, and no user content in any classification or in
 * any message derived from one. The database raises custom SQLSTATEs in the
 * reserved `EV4xx` class; those are mapped here rather than pattern-matched from
 * message strings.
 */

export type EvidenceFailureReason =
  /** Malformed request: bad uuid, bad state, missing governed field. */
  | "validation_failed"
  /** Not found, or in another workspace — deliberately indistinguishable. */
  | "not_found_or_not_authorized"
  /** Active member who simply lacks the capability. */
  | "role_not_permitted"
  /** Caller's account is not active. */
  | "account_not_active"
  /** Workspace is archived. */
  | "workspace_not_active"
  /** The requested state change is not in the transition matrix. */
  | "invalid_transition"
  /** Optimistic concurrency lost. */
  | "version_conflict"
  /** Same idempotency key, different mutation, or a spent key. */
  | "idempotency_conflict"
  /** Bytes are absent at the expected object path. */
  | "object_missing"
  /** Bytes exceed the governed ceiling. */
  | "object_too_large"
  /** Detected format is not one of the four allowed formats. */
  | "format_not_allowed"
  /** Advisory declared MIME disagreed with the detected format. */
  | "declared_mime_mismatch"
  /** No scanner acceptance available: quarantine, never acceptance. */
  | "scanner_not_accepted"
  /** Evidence is not in a state that may support resolution. */
  | "evidence_not_usable"
  /** Condition is in another Loop or workspace, or does not exist. */
  | "condition_not_applicable"
  /** Transport or provider failure. Safe to retry the identical request. */
  | "temporarily_unavailable"
  /** Anything unclassified. Never retried automatically. */
  | "unknown";

const SQLSTATE_MAP: Readonly<Record<string, EvidenceFailureReason>> = {
  EV400: "role_not_permitted",
  EV401: "not_found_or_not_authorized",
  EV409: "version_conflict",
  EV410: "invalid_transition",
  EV411: "evidence_not_usable",
  EV412: "idempotency_conflict",
  EV413: "validation_failed",
  EV414: "workspace_not_active",
  EV415: "account_not_active",
  EV416: "condition_not_applicable",
};

const RETRYABLE: readonly EvidenceFailureReason[] = ["temporarily_unavailable"];

export function isRetryableEvidenceReason(reason: EvidenceFailureReason): boolean {
  return RETRYABLE.includes(reason);
}

interface MaybePostgrestError {
  readonly code?: unknown;
}

export function classifyEvidenceError(error: unknown): EvidenceFailureReason {
  if (error !== null && typeof error === "object") {
    const code = (error as MaybePostgrestError).code;
    if (typeof code === "string") {
      const mapped = SQLSTATE_MAP[code];
      if (mapped) return mapped;
      // PostgREST/transport classes that a retry of the identical request can
      // legitimately clear.
      if (code === "500" || code === "502" || code === "503" || code === "504") {
        return "temporarily_unavailable";
      }
    }
  }
  if (error instanceof Error && /network|fetch failed|timeout|aborted/i.test(error.message)) {
    return "temporarily_unavailable";
  }
  return "unknown";
}

/** Safe, non-leaking user-facing copy. One line per reason, no provider detail. */
export function evidenceFailureMessage(reason: EvidenceFailureReason): string {
  switch (reason) {
    case "validation_failed":
      return "That evidence request was not valid.";
    case "not_found_or_not_authorized":
      return "That evidence is not available to you.";
    case "role_not_permitted":
      return "Your role cannot manage evidence in this workspace.";
    case "account_not_active":
      return "Your account is not active.";
    case "workspace_not_active":
      return "This workspace is archived.";
    case "invalid_transition":
      return "That evidence step is not allowed from its current state.";
    case "version_conflict":
      return "This evidence changed elsewhere. Reload and try again.";
    case "idempotency_conflict":
      return "That request was already used. Start a new one.";
    case "object_missing":
      return "The uploaded file could not be found.";
    case "object_too_large":
      return "That file is larger than 10 MB.";
    case "format_not_allowed":
      return "Only PDF, PNG, JPEG and plain text files are accepted.";
    case "declared_mime_mismatch":
      return "That file's contents did not match its declared type.";
    case "scanner_not_accepted":
      return "This file is being held for safety checks.";
    case "evidence_not_usable":
      return "That evidence cannot support a resolution yet.";
    case "condition_not_applicable":
      return "That evidence is not linked to this resolution condition.";
    case "temporarily_unavailable":
      return "Evidence is temporarily unavailable. Try again.";
    case "unknown":
    default:
      return "Something went wrong handling that evidence.";
  }
}
